<template>
  <!-- Steth Guidance Popup -->
  <div
    role="list"
    class="
      max-w-lg
      m-1
      mr-0
      p-4
      border border-gray-200
      shadow
      absolute
      right-0
      z-20
      rounded-2xl
      bg-gray-50
    "
    :class="rowLocal === 2 ? 'bottom-136' : 'bottom-68'"
  >
    <div class="w-full flex-col font-medium text-md text-gray-900 justify-between leading-7 gap-3">
      <!-- Header topline -->
      <div class="flex gap-2 justify-between w-full">
        <div class="inline-flex gap-3">
          <!-- Steth Icon -->
          <div class="flex items-center rounded-xl bg-vice-teal-50 p-1 mr-1 h-10 w-10">
            <div
              class="w-3 h-3 absolute top-3.5 left-12 rounded-full bg-vice-teal-500 opacity-75"
            />
            <ViceStethoscope pathFill="#0D9488" class="h-12 w-12" />
          </div>
          <div class="flex flex-col items-start">
            <span class="font-semibold text-xs">Stethoscope detected</span>
            <div class="text-left inline-flex">
              <ExclamationIcon class="w-5 h-5 mr-1" fill="#ea5264" />
              <span class="text-gray-500 leading-5 text-xxs">
                The stethoscope
                <span class="font-bold text-gray-700">starts paused by default</span>
                - press play to begin transmitting audio.
              </span>
            </div>
          </div>
        </div>
        <div>
          <button @click="closeToast">
            <CrossSimpleIcon type="link" class="text-vice-dark-grey hover:text-strong-grey" />
          </button>
        </div>
      </div>

      <!-- Header content -->
      <div class="inline-flex items-center gap-2 w-full mt-4 ml-2">
        <!-- Dropdown arrow -->
        <button
          class="text-xxs font-semibold inline-flex items-center gap-1 px-1"
          @click="isGuideOpen = !isGuideOpen"
        >
          Setup help
          <ChevronUpIcon
            v-if="isGuideOpen"
            class="transition-transform duration-300 w-5 h-5 self-center text-gray-500"
          />
          <ChevronDownIcon
            v-else
            class="transition-transform duration-300 w-5 h-5 self-center text-gray-500"
          />
        </button>

        <!-- Full guide link -->
        <ViceButton
          class="text-xxs font-semibold ml-2 border border-vice-medium-grey"
          white
          small
          :href="stethSupportLink"
          target="_blank"
        >
          Full guide
          <ExternalLinkIcon class="h-4 w-4" />
        </ViceButton>
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-out overflow-hidden"
        leave-active-class="transition-all duration-300 ease-in overflow-hidden"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-screen opacity-100"
        leave-from-class="max-h-screen opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <div v-show="isGuideOpen">
          <!-- Advice content -->
          <div class="container inline-flex justify-between text-sm rounded-xl">
            <div class="block col-md-6 font-medium text-gray-700">
              <ul ref="liItems">
                <li
                  class="
                    mt-3
                    mb-2
                    flex
                    gap-3
                    text-left
                    border border-gray-200
                    shadow
                    px-3
                    py-4
                    rounded-xl
                    bg-white
                  "
                  ref="liIcon"
                >
                  <div class="flex items-center rounded-lg bg-gray-100 p-2 mr-1 h-7 w-7">
                    <UsbIcon pathFill="#7C7878" class="h-3 w-3" />
                  </div>
                  <div class="tooltip flex-col">
                    <span class="font-semibold text-xxs">1. Power on & attach splitter</span>
                    <p class="text-xxs text-gray-500">
                      Press play button to wake the stethoscope, then plug in the splitter.
                    </p>
                  </div>
                </li>
                <li
                  class="
                    mt-3
                    mb-2
                    flex
                    gap-3
                    text-left
                    border border-gray-200
                    shadow
                    px-3
                    py-4
                    rounded-xl
                    bg-white
                  "
                  ref="liIcon"
                >
                  <div class="flex items-center rounded-lg bg-gray-100 h-7 w-7">
                    <ViceStethoscope pathFill="#7C7878" class="h-7 w-7" />
                  </div>
                  <div class="tooltip flex-col">
                    <span class="font-semibold text-xxs">2. Plug in headphones</span>
                    <p class="text-xxs text-gray-500">
                      Connect headphones to the splitter’s headphone jack.
                    </p>
                  </div>
                </li>
                <li
                  class="
                    mt-3
                    mb-2
                    flex
                    gap-3
                    text-left
                    border border-gray-200
                    shadow
                    px-3
                    py-4
                    rounded-xl
                    bg-white
                  "
                  ref="liIcon"
                >
                  <div class="flex items-center rounded-lg bg-gray-100 p-2 mr-1 h-7 w-7">
                    <ViceSpeakers pathFill="#7C7878" class="h-3 w-3" />
                  </div>
                  <div class="tooltip flex-col">
                    <span class="font-semibold text-xxs">3. Connect to computer</span>
                    <p class="text-xxs text-gray-500">
                      Stethoscope → splitter -> Audio cable → red mic port on USB adapter →
                      computer.
                    </p>
                  </div>
                </li>
                <li
                  class="
                    mt-3
                    mb-2
                    flex
                    gap-3
                    text-left
                    border border-gray-200
                    shadow
                    px-3
                    py-4
                    rounded-xl
                    bg-white
                  "
                  ref="liIcon"
                >
                  <div class="flex items-center rounded-lg bg-gray-100 p-2 mr-1 h-7 w-7">
                    <HeadsetOn pathFill="#7C7878" class="h-3 w-3" />
                  </div>
                  <div class="tooltip flex-col">
                    <span class="font-semibold text-xxs">4. No audio? Troubleshoot</span>
                    <ul class="text-xxs text-gray-500">
                      <li class="ml-4 list-disc">Stethoscope powered on?</li>
                      <li class="ml-4 list-disc">Cable in the red (mic) port?</li>
                      <li class="ml-4 list-disc">Headphones connected?</li>
                    </ul>
                  </div>
                </li>
                <li
                  class="
                    mt-3
                    flex
                    gap-3
                    text-left
                    border border-gray-200
                    shadow
                    px-3
                    py-4
                    rounded-xl
                    bg-white
                  "
                  ref="liIcon"
                >
                  <div class="flex items-center rounded-lg bg-gray-100 p-2 mr-1 h-7 w-7">
                    <AudioMixerIcon pathFill="#7C7878" class="h-3 w-3" />
                  </div>
                  <div class="tooltip flex-col">
                    <span class="font-semibold text-xxs">5. Echo?</span>
                    <p class="text-xxs text-gray-500">
                      Switch off playback in the stethoscope stream.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from '@heroicons/vue/solid';
import { ref } from 'vue';
import CrossSimpleIcon from '@/Components/Icons/CrossSimpleIcon.vue';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import ExclamationIcon from '@/Components/Icons/ExclamationIcon.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import useSessionStore from '@/Stores/useSessionStore';
import UsbIcon from '@/Components/Icons/UsbIcon.vue';
import ViceSpeakers from '@/Components/Icons/ViceSpeakers.vue';
import HeadsetOn from '@/Components/Icons/HeadsetOn.vue';
import AudioMixerIcon from '@/Components/Icons/AudioMIxerIcon.vue';

export default {
  name: 'StethoscopeGuidancePopup',
  props: {
    rowLocal: {
      type: Number,
    },
  },
  components: {
    AudioMixerIcon,
    HeadsetOn,
    ViceSpeakers,
    UsbIcon,
    ViceButton,
    ExclamationIcon,
    ChevronDownIcon,
    ExternalLinkIcon,
    ChevronUpIcon,
    ViceStethoscope,
    CrossSimpleIcon,
  },
  emits: ['toggleStethGuide'],
  setup() {
    const isGuideOpen = ref(false);
    const sessionStore = useSessionStore();
    const stethSupportLink =
      'https://support.virtualce.app/en/articles/9776249-getting-started-with-the-vce-stethoscope-thinklabs-one';

    const closeToast = () => {
      sessionStore.showStethGuide = false;
    };

    return { isGuideOpen, stethSupportLink, closeToast };
  },
};
</script>
<style scoped></style>
